/**
 * Storage utility for localStorage and cookies
 * UNIFIED storage layer for both tracker and chatbot
 */

export class Storage {
  // Unified prefix for all chatbot/tracker data
  private prefix = 'chatbot_';

  /**
   * Set item in localStorage (flat structure, no wrapper)
   */
  setLocal<T>(key: string, value: T, expiresInMinutes?: number): void {
    try {
      // Store flat object with optional _expires field
      const data: any = typeof value === 'object' && value !== null 
        ? { ...value }
        : { _value: value };
      
      if (expiresInMinutes) {
        data._expires = Date.now() + (expiresInMinutes * 60 * 1000);
      }
      
      localStorage.setItem(this.prefix + key, JSON.stringify(data));
    } catch (error) {
    }
  }

  /**
   * Get item from localStorage
   */
  getLocal<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(this.prefix + key);
      if (!item) return null;

      const data = JSON.parse(item);
      
      // Check expiration
      if (data._expires && Date.now() > data._expires) {
        this.removeLocal(key);
        return null;
      }

      // If it was a primitive wrapped in _value, unwrap it
      if (data._value !== undefined && Object.keys(data).filter(k => !k.startsWith('_')).length === 0) {
        return data._value as T;
      }

      // Return the object without internal fields
      const { _expires, _value, ...rest } = data;
      return (Object.keys(rest).length > 0 ? rest : data._value) as T;
    } catch (error) {
      return null;
    }
  }

  /**
   * Remove item from localStorage
   */
  removeLocal(key: string): void {
    try {
      localStorage.removeItem(this.prefix + key);
    } catch (error) {
    }
  }

  /**
   * Clear all chatbot data from localStorage
   */
  clearAll(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
    }
  }

  /**
   * Set cookie
   */
  setCookie(name: string, value: string, days: number = 30): void {
    try {
      const expires = new Date();
      expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
      
      document.cookie = `${this.prefix}${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
    } catch (error) {
    }
  }

  /**
   * Get cookie
   */
  getCookie(name: string): string | null {
    try {
      const nameEQ = `${this.prefix}${name}=`;
      const cookies = document.cookie.split(';');
      
      for (let i = 0; i < cookies.length; i++) {
        let cookie = cookies[i].trim();
        if (cookie.indexOf(nameEQ) === 0) {
          return decodeURIComponent(cookie.substring(nameEQ.length));
        }
      }
      
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Remove cookie
   */
  removeCookie(name: string): void {
    try {
      document.cookie = `${this.prefix}${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
    } catch (error) {
    }
  }

  /**
   * Check if localStorage is available
   */
  isLocalStorageAvailable(): boolean {
    try {
      const test = '__test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if cookies are enabled
   */
  areCookiesEnabled(): boolean {
    try {
      document.cookie = 'testcookie=1';
      const enabled = document.cookie.indexOf('testcookie=') !== -1;
      document.cookie = 'testcookie=1;expires=Thu, 01 Jan 1970 00:00:00 UTC';
      return enabled;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get the storage prefix (for external access)
   */
  getPrefix(): string {
    return this.prefix;
  }
}
